import { Button, Dropdown, TableColumnsType, TablePaginationConfig, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { InfoTableHelper, WrapperPage } from '@components/atoms';
import { GenericList } from '@components/molecules';
import { useJobApplications } from '@hooks';
import { getJobApplicationsWithFilters } from '@hooks/useJobApplications/useJobApplications';
import { DownloadSimple, PencilSimple, Trash } from '@phosphor-icons/react';

const { Title, Paragraph } = Typography;

enum OrderEnum {
  ascend = 'asc',
  descend = 'desc',
}

type FilterType = 'All' | 'Operations' | 'Management' | 'Snow Removal' | 'Maintenance' | 'Administration';

type ApplicationStatus = 'pending' | 'reviewed' | 'contacted' | 'rejected' | 'hired';

export const TalentPoolPage = () => {
  const { updateJobApplicationStatus, deleteJobApplication, getResumeUrl } = useJobApplications();

  const [activeFilter, setActiveFilter] = useState<FilterType>('All');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>('updatedAt');
  const [sortOrder, setSortOrder] = useState<string | undefined>('descend');

  const params = {
    page,
    limit,
    search,
    department: activeFilter !== 'All' ? activeFilter : undefined,
    orderBy: sortField,
    orderType: sortOrder ? OrderEnum[sortOrder as keyof typeof OrderEnum] : undefined,
  };

  // Fetch applications using the hook with filters
  const { data, isLoading, refetch } = getJobApplicationsWithFilters(params);

  // Refetch quando os parâmetros mudam
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      refetch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, limit, search, sortOrder, sortField, activeFilter, refetch]);

  // Check if data exists before accessing properties
  const applications = Array.isArray(data) ? data : [];
  const total = applications.length || 0;

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: any,
    sorter: any
  ) => {
    const { current, pageSize } = pagination;

    if (pageSize !== limit) {
      setLimit(pageSize || 10);
      setPage(1);
      return;
    }

    if (current !== page) {
      setPage(current || 1);
      return;
    }

    // Verifique se sorter é um array e use o primeiro elemento se for
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    // Verifique se activeSorter tem propriedade field ou order antes de continuar
    if (!activeSorter || !activeSorter.field || !activeSorter.order) {
      return;
    }

    if (activeSorter.field !== sortField || activeSorter.order !== sortOrder) {
      setSortField(activeSorter.field as string);
      setSortOrder(activeSorter.order as string);
      setPage(1);
    }
  };

  const handleStatusChange = (id: string, status: ApplicationStatus) => {
    updateJobApplicationStatus.mutate({ id, data: { status } });
  };

  const columns: TableColumnsType<any> = [
    {
      title: 'CANDIDATE',
      key: 'candidate',
      dataIndex: 'fullName',
      render: (_: any, record: any) => (
        <InfoTableHelper
          title={record.fullName}
          subTitle={record.email}
        />
      ),
      sorter: true,
    },
    {
      title: 'POSITION',
      key: 'position',
      dataIndex: ['position', 'title'],
      render: (_: any, record: any) => (
        <span className="capitalize !font-['Barlow']">{record.position?.title}</span>
      ),
      sorter: true,
    },
    {
      title: 'SALARY',
      key: 'salary',
      dataIndex: ['position', 'salaryRange'],
      render: (_: any, record: any) => (
        <span className="capitalize !font-['Barlow']">{record.position?.salaryRange}</span>
      ),
      sorter: true,
    },
    {
      title: 'DEPARTMENT',
      key: 'department',
      dataIndex: ['position', 'department'],
      render: (_: any, record: any) => (
        <span className="capitalize !font-['Barlow']">{record.position?.department}</span>
      ),
      sorter: true,
    },
    {
      title: 'LOCATION',
      key: 'location',
      dataIndex: ['position', 'location'],
      render: (_: any, record: any) => (
        <span className="capitalize !font-['Barlow']">{record.position?.location}</span>
      ),
      sorter: true,
    },
    {
      title: 'STATUS',
      key: 'status',
      dataIndex: 'status',
      render: (_: any, record: any) => (
        <Dropdown
          menu={{
            items: [
              { key: 'pending', label: 'Pending' },
              { key: 'reviewed', label: 'Reviewed' },
              { key: 'contacted', label: 'Contacted' },
              { key: 'rejected', label: 'Rejected' },
              { key: 'hired', label: 'Hired' },
            ].map(status => ({
              key: status.key,
              label: (
                <span
                  className={`capitalize ${record.status === status.key ? 'text-[#E3AF25]' : ''}`}
                  onClick={() => handleStatusChange(record.id, status.key as ApplicationStatus)}
                >
                  {status.label}
                </span>
              ),
            })),
          }}
          trigger={['click']}
        >
          <Button type="text" className="!text-white hover:!text-[#E3AF25]">
            <span className="capitalize">{record.status}</span>
          </Button>
        </Dropdown>
      ),
      sorter: true,
    },
    {
      title: 'APPLIED AT',
      key: 'createdAt',
      dataIndex: 'createdAt',
      render: (_: any, record: any) =>
        record.createdAt ? dayjs(record.createdAt).format('MM/DD/YYYY') : '-',
      sorter: true,
    },
    {
      title: 'LAST UPDATED',
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      render: (_: any, record: any) =>
        record.updatedAt ? dayjs(record.updatedAt).format('MM/DD/YYYY - HH:mm') : '-',
      sorter: true,
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 120,
      render: (_: any, record: any) => (
        <div className="flex gap-2">
          <Tooltip title="Edit Status">
            <Dropdown
              menu={{
                items: [
                  { key: 'pending', label: 'Pending' },
                  { key: 'reviewed', label: 'Reviewed' },
                  { key: 'contacted', label: 'Contacted' },
                  { key: 'rejected', label: 'Rejected' },
                  { key: 'hired', label: 'Hired' },
                ].map(status => ({
                  key: status.key,
                  label: (
                    <span
                      className={`capitalize ${record.status === status.key ? 'text-[#E3AF25]' : ''}`}
                      onClick={() => handleStatusChange(record.id, status.key as ApplicationStatus)}
                    >
                      {status.label}
                    </span>
                  ),
                })),
              }}
              trigger={['click']}
            >
              <Button
                icon={<PencilSimple size={16} />}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                type="text"
                className="!text-[#E3AF25] [&_svg]:!text-[#E3AF25] [&_svg]:!fill-[#E3AF25] [&:hover_svg]:transition-all [&:hover_svg]:duration-200 [&:hover_svg]:drop-shadow-[0_0_8px_#E3AF25]"
              />
            </Dropdown>
          </Tooltip>
          {record.resume && (
            <Tooltip title="Download Resume">
              <Button
                icon={<DownloadSimple size={16} />}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(getResumeUrl(record.resume), '_blank');
                }}
                type="text"
                className="!text-[#E3AF25] [&_svg]:!text-[#E3AF25] [&_svg]:!fill-[#E3AF25] [&:hover_svg]:transition-all [&:hover_svg]:duration-200 [&:hover_svg]:drop-shadow-[0_0_8px_#E3AF25]"
              />
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <Button
              icon={<Trash size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                deleteJobApplication.mutate(record.id);
              }}
              type="text"
              danger
              className="!text-red-500 [&_svg]:!text-red-500 [&_svg]:!fill-red-500 [&:hover_svg]:transition-all [&:hover_svg]:duration-200 [&:hover_svg]:drop-shadow-[0_0_8px_#ef4444]"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative w-full h-[400px]">
      <div className="careers-bg opacity-40 absolute inset-0 w-full h-full"></div>
        <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 md:py-16 lg:py-20 z-10">
          <Title level={1} className="font-android !font-normal text-yellow-principal text-center">
            TALENT POOL
          </Title>
          <div className="max-w-3xl mx-auto">
            <Paragraph className="font-barlow text-gray-300 text-center md:text-lg lg:!text-xl">
              View and manage all job applications
            </Paragraph>
          </div>
        </div>
      </section>

      <section className="w-full py-15 bg-[#121212]">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-6">
            <Title level={2} className="!text-white !font-['Android_101'] !font-normal m-0">TALENT POOL</Title>
          </div>

          <Paragraph className="text-gray-300 !font-['Barlow'] mb-8">
            View and manage all job applications
          </Paragraph>

          {/* Filtros por tipo de vaga */}
          <div className="flex flex-wrap justify-start gap-2 mb-12">
            {(['All', 'Operations', 'Management', 'Snow Removal', 'Maintenance', 'Administration'] as FilterType[]).map((filter) => (
              <Button
                key={filter}
                type="default"
                className={`filter-button ${activeFilter === filter ? 'filter-active' : ''}`}
                onClick={() => {
                  setActiveFilter(filter);
                }}
              >
                {filter}
              </Button>
            ))}
          </div>

          <WrapperPage isLoading={isLoading}>
            <GenericList<any>
              columns={columns}
              dataSource={applications}
              rowKey={(record) => record?.id || Math.random().toString()}
              expandable={{
                expandedRowRender: (record) => (
                  <div className="px-2 py-1">
                    <p className="text-[#E3AF25] text-xs uppercase tracking-widest mb-1 !font-['Barlow']">Message</p>
                    <p className="text-gray-200 whitespace-pre-wrap !font-['Barlow']">
                      {record.message?.trim() ? record.message : 'No message provided'}
                    </p>
                    {record.phone && (
                      <p className="text-gray-400 mt-3 !font-['Barlow']">Phone: {record.phone}</p>
                    )}
                  </div>
                ),
                rowExpandable: (record) => Boolean(record.message || record.phone),
              }}
              onRow={(record) => ({
                onClick: () => record,
              })}
              onChange={handleTableChange}
              onSearch={handleSearch}
              pagination={{
                current: page,
                pageSize: limit,
                total: total,
                onChange(page, pageSize) {
                  setPage(page);
                  setLimit(pageSize || 10);
                },
                showSizeChanger: true,
              }}
            />
          </WrapperPage>
        </div>
      </section>
    </div>
  );
};
